import React from 'react';
import { Text } from '@react-three/drei';

// WelcomeSign — the first thing a visitor reads, standing where they arrive.
//
// The city is open while it is being built, which means people walk into empty
// rooms, unfinished streets and things that move between visits. Said plainly
// up front, that reads as an invitation. Left unsaid, the same city reads as
// broken — the visitor assumes they are seeing a finished thing done badly.
//
// So this is deliberately honest rather than promotional: it names what is
// missing before anyone discovers it themselves. It also sets the return hook —
// "it won't be the same place" is the reason to come back tomorrow.
//
// Placed in world space rather than as HUD copy so it belongs to the street and
// can be walked past, not dismissed. Tune live: ?signx ?signy ?signz ?signrot
// ?signscale — then paste the values into the defaults below.
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};

const ACCENT = '#ffb25e';
const TEXT = '#f2e6d4';

// Separately POSITIONED blocks rather than one string with blank lines.
// Blank lines make the block taller, and because drei's Text centres on its
// anchor, a taller block grows UPWARD as well as down — which is how the body
// climbed over the subheader. Explicit y per block means adding a line can
// never move the one above it.
//
// Read at walking pace by someone who did not come here to read. Six lines,
// each carrying one idea: you're early · it's unfinished · but it's live ·
// come back. Anything longer gets skipped, and a skipped sign is the same as
// no sign — which is the failure this exists to prevent.
//
// Board is 2.3 tall (±1.15). Block centres, top to bottom:
//   title 0.82 · subhead 0.58 · lead 0.31 · body 0.0 · live −0.48 · off −0.97
const LEAD = "You're early, and that's the point.";
const BODY = 'A framework, not a finished city. It changes daily.';
// ACCENT, because this is the part that argues the place is real rather than a
// mockup — and it is the one claim a visitor can verify on the spot. "Empty
// rooms" reads very differently once you know the clock and the radio are live.
const LIVE = [
  'The clock runs on real Oakland time, though,',
  'and the radio is genuinely playing.',
  'One day all of it will be live.',
].join('\n');
// Closes on the promise rather than the disclaimer. Everything above admits
// what is missing; this is the line that says why it is worth your time anyway —
// and it is the one a visitor will still be carrying when they leave.
const SIGNOFF = 'The city lives, and the artists remember you';

export default function WelcomeSign() {
  // Anchored to where you ACTUALLY land in ?city=model — PlanetScene's
  // MODEL_WALK_START, pos [0.18, EYE, 25.53] looking toward z 23.65, i.e. you
  // arrive deep in +z facing −z. (STREET_OVERVIEW is the other, non-model entry;
  // reading that one instead put this sign 31m behind the town and facing away.)
  // A few metres ahead along the view direction, just above eye line.
  //
  // MOVED TO THE KERB (2026-10-11). Dead ahead at 4 units the 3.5-wide board
  // filled the whole first frame — on a phone, the ENTIRE screen, so a new
  // player's first look at the city was a wall of text. It now stands at the
  // right-hand kerb, angled toward the arrival point: still the first thing you
  // read, still something you walk past rather than dismiss, but the street,
  // the Lotus and the marquee are in the shot with it.
  const pos = [qp('signx', 2.9), qp('signy', 2.2), qp('signz', 18.5)];
  // A plane's normal is +z, and the viewer stands at +z of the sign. Turned
  // −0.55 rad so its face swings toward the arrival point from the kerb.
  const rotY = qp('signrot', -0.55);
  const scale = qp('signscale', 1);

  return (
    <group position={pos} rotation={[0, rotY, 0]} scale={scale}>
      {/* Board. Dark and matte so the text carries, and unlit enough to stay
          readable at any hour — a welcome that only works at noon is worse
          than none. */}
      <mesh position={[0, 0, -0.03]}>
        <planeGeometry args={[3.5, 2.3]} />
        <meshStandardMaterial color="#140e08" roughness={0.9} metalness={0} />
      </mesh>
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[3.38, 2.18]} />
        <meshStandardMaterial color="#1e150c" roughness={0.85} metalness={0} />
      </mesh>

      <Text
        position={[0, 0.82, 0]} fontSize={0.185} color={ACCENT}
        anchorX="center" anchorY="middle" letterSpacing={0.07} outlineWidth={0}
      >
        WELCOME TO HYPETOWN
      </Text>
      <Text
        position={[0, 0.58, 0]} fontSize={0.082} color="#b8956b"
        anchorX="center" anchorY="middle" letterSpacing={0.05}
      >
        — OPEN WHILE WE BUILD IT —
      </Text>
      <Text
        position={[0, 0.31, 0]} fontSize={0.115} color={TEXT}
        anchorX="center" anchorY="middle" textAlign="center" maxWidth={3.1}
      >
        {LEAD}
      </Text>
      <Text
        position={[0, 0, 0]} fontSize={0.11} color={TEXT}
        anchorX="center" anchorY="middle" lineHeight={1.45} textAlign="center" maxWidth={3.1}
      >
        {BODY}
      </Text>
      <Text
        position={[0, -0.48, 0]} fontSize={0.11} color={ACCENT}
        anchorX="center" anchorY="middle" lineHeight={1.45} textAlign="center" maxWidth={3.1}
      >
        {LIVE}
      </Text>
      <Text
        position={[0, -0.97, 0]} fontSize={0.115} color={ACCENT}
        anchorX="center" anchorY="middle" lineHeight={1.45} textAlign="center" maxWidth={3.1}
      >
        {SIGNOFF}
      </Text>

      {/* A little warmth on the board so it reads as lit signage rather than a
          floating panel. Short range — it must not spill onto the street. */}
      <pointLight position={[0, 0.2, 0.9]} color={ACCENT} intensity={2.2} distance={3.2} />
    </group>
  );
}
